/**
 * CorrectEmailDialog - fix a mistyped address before it has been confirmed.
 *
 * Offered only while an account is unconfirmed, and the server enforces the
 * same rule. Once an address has been confirmed it becomes the login identity
 * and the delivery address at once, and letting a signed-in session repoint it
 * would mean a borrowed laptop could walk an account away from its owner.
 *
 * Before confirmation none of that applies, and leaving the typo is what
 * actually costs the owner: the confirmation never arrives, alerts never
 * arrive, and a forgotten password can never be reset because the reset goes
 * to an address they do not control.
 *
 * The password is asked for here for the same reason the server asks: without
 * it, a borrowed session could still repoint an unconfirmed account.
 */

import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Typography, Box, Alert as MuiAlert, InputAdornment, IconButton,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useAuth } from '../context/AuthContext';
import { useThemeMode } from '../context/ThemeContext';
import { useRecipes } from '../theme/recipes';
import { correctEmail } from '../services/auth';

function CorrectEmailDialog({ open, onClose, onCorrected }) {
  const { user, applyUserPatch } = useAuth();
  const { palette } = useThemeMode();
  const recipes = useRecipes();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setEmail('');
    setPassword('');
    setError('');
    setSaving(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    const next = email.trim().toLowerCase();
    if (next === (user?.email || '').trim().toLowerCase()) {
      setError('That is the address you already have.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const updated = await correctEmail(next, password);
      applyUserPatch({ email: updated.email });
      reset();
      onCorrected?.(updated.email);
      onClose();
    } catch (e) {
      setError(e?.message || 'We could not change that address.');
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      PaperProps={{ sx: { ...recipes.dialogPaperProps.sx, maxWidth: 460, width: '100%' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>Correct your email address</DialogTitle>
      <DialogContent dividers>
        {error && <MuiAlert severity="error" sx={{ mb: 2 }}>{error}</MuiAlert>}

        <Typography sx={{ fontSize: '0.85rem', color: palette.textSecondary, lineHeight: 1.6, mb: 2 }}>
          We are sending confirmation to{' '}
          <Box component="span" sx={{ fontWeight: 700, color: palette.text }}>{user?.email}</Box>.
          If that is not right, change it here and we will send a fresh link to the new
          address. You can only do this until an address is confirmed.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Your correct email address"
            type="email"
            size="small"
            fullWidth
            autoFocus
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            InputLabelProps={{ sx: recipes.labelSx }}
            InputProps={{ sx: recipes.controlSx }}
          />
          <TextField
            label="Your password"
            type={showPw ? 'text' : 'password'}
            size="small"
            fullWidth
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            helperText="So we know it is you making the change"
            InputLabelProps={{ sx: recipes.labelSx }}
            InputProps={{
              sx: recipes.controlSx,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPw((v) => !v)}
                    sx={{ color: palette.textMuted }}
                  >
                    <Icon icon={showPw ? 'ph:eye-slash' : 'ph:eye'} width={16} height={16} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={close} sx={{ color: palette.textMuted, textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={submit}
          disabled={saving || !email.trim() || !password}
          sx={{ ...recipes.primaryBtnSx, textTransform: 'none' }}
        >
          {saving ? 'Saving…' : 'Change and resend'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CorrectEmailDialog;
